import React from 'react';
import List, {defaultFilters} from '../cruds/List';
import View from '../cruds/View';
import Edit from '../cruds/Edit';
import {notNull, stringNotBlank} from '../cruds/validators';
import Iterate from '../components/container/Iterate';
import {OutputAttachmentRow, OutputAttachmentRowDisabled} from '../components/output/OutputAttachments';
import OutputBox, {OutputBoxBoolean, OutputBoxContent,} from '../components/output/OutputBox';
import {
    FULL_SDS_EVALUATION_ENABLED,
    R_PHRASES_AND_SCORE_P_VISIBLE,
    SDS_EXPORT_ENABLED,
    ShowBasedOnConfig,
    SUBSTANCE_ADR_VISIBLE
} from '../components/container/ShowBasedOnConfig';
import OutputText, {SimpleOutputText} from '../components/output/OutputText';
import OutputList, {SimpleOutputList} from '../components/output/OutputList';
import OutputValue, {LocalizedOutputValue} from '../components/output/OutputValue';
import InputText from '../components/input/InputText';
import InputTextAutocomplete from '../components/input/InputTextAutocomplete';
import RemoteInputDualList from '../components/input/RemoteInputDualList';
import RemoteInputSelect from '../components/input/RemoteInputSelect';
import InputSelect from '../components/input/InputSelect';
import CollapsibleRowList from '../components/container/CollapsibleRow';
import MainContainer from '../components/MainContainer';
import {fetchPost} from '../cruds/remote';
import {Horizontal, Vertical} from '../components/container/LayoutRow';
import {Route, Switch} from 'react-router-dom';
import WithRole, {withRoles} from '../components/container/WithRole';
import './Substances.css';
import {OutputLink} from '../components/output/OutputLink';
import getLabel from '../translations/messages';
import {
    ArchiveSafetyDataSheetButton,
    NewSafetyDataSheetButton,
    NewSafetyDataSheetEvaluationButton,
    ViewSafetyDataSheetButton,
    ViewSafetyDataSheetEvaluationButton
} from './SubstancesButtons';
import SafetyDataSheets from './SafetyDataSheets';
import SafetyDataSheetEvaluations from './SafetyDataSheetEvaluations';
import SearchResultPanelSDS from './SearchResultPanelSDS';
import DownloadButton from "../components/output/DownloadButton";
import {ShowBasedOnCondition} from "../components/container/ShowBasedOnCondition";

const ENTITY_NAME = 'substances';
const TITLE = 'substances.title';

const evaluate = (resource) => {
    return fetchPost(ENTITY_NAME + '/reevaluation-needed', resource);
};

const LastSafetyDataSheetText = ({style}, {locale}) => (
    <span style={style}>{getLabel(locale, 'substances.sdsLong') + ': '}</span>
);

const LastEvaluationText = ({style}, {locale}) => (
    <span style={style}>{getLabel(locale, 'evaluations.evaluation') + ': '}</span>
);

const CrudEdit = props => {
    const validators = [
        notNull('agentId', 'substances.err.agent'),
        stringNotBlank('physicalState', 'substances.err.physicalState'),
        stringNotBlank('supplier', 'substances.err.supplier'),
    ];
    return (
        <Edit {...props}
              title={TITLE}
              entityName={ENTITY_NAME}
              entityRoute={ENTITY_NAME}
              entityId={props.match.params.number}
              validators={validators}
              preSubmit={(resource) => evaluate(resource)}
        >
            <SimpleOutputText name="code"/>
            <InputText name="supplierCode" label="substances.supplier.code"/>
            <RemoteInputSelect from="agents"
                               order="DESCRIPTION"
                               optionValueName="id"
                               optionLabelName="description"
                               name="agentId"
                               label="substances.agent"
                               required/>
            <InputSelect name="physicalState"
                         label="substances.physicalState"
                         options={[
                             {value: 'substances.solid', label: 'substances.solid'},
                             {value: 'substances.liquid', label: 'substances.liquid'},
                             {value: 'substances.gas', label: 'substances.gas'},
                         ]}
                         translateValue
                         required
            />
            <InputTextAutocomplete from="substances/suppliers" name="supplier" label="substances.supplier.name"
                                   required/>
            <ShowBasedOnConfig configKey={R_PHRASES_AND_SCORE_P_VISIBLE} name="rPhrases">
                <RemoteInputDualList from="phrases?type=R&order=CODE" optionLabelName="code" name="rPhrases"
                                     label="substances.rPhrases"/>
            </ShowBasedOnConfig>
            <RemoteInputDualList from="phrases?type=H&order=CODE" optionLabelName="code" name="hPhrases"
                                 label="substances.hPhrases"/>
            <ShowBasedOnConfig name="adr" configKey={SUBSTANCE_ADR_VISIBLE}>
                <InputText label="substances.adr"/>
            </ShowBasedOnConfig>
        </Edit>
    );
};

const CrudView = props => (
    <View title={TITLE}
          entityName={ENTITY_NAME}
          entityRoute={ENTITY_NAME}
          entityId={props.match.params.number}
          crudClassName="substances-view"
    >
        <Horizontal>
            <Vertical style={{flex: 1}}>
                <OutputText name="code" label="substances.idCode"/>
                <OutputText name="supplierCode" label="substances.supplier.code"/>
                <OutputText name="agentDescription" label="substances.agent"/>
                <OutputText name="physicalState" label="substances.physicalState"/>
                <OutputText name="supplier" label="substances.supplier.name"/>
                <OutputText name="scoreH" label="substances.hScore"/>
                <ShowBasedOnConfig configKey={R_PHRASES_AND_SCORE_P_VISIBLE}>
                    <OutputText name="scoreP" label="substances.pScore"/>
                </ShowBasedOnConfig>
            </Vertical>
            <Vertical style={{flex: 1}}>
                <ShowBasedOnConfig configKey={R_PHRASES_AND_SCORE_P_VISIBLE}>
                    <OutputList name="rPhrasesCode" label="substances.rPhrases"/>
                </ShowBasedOnConfig>

                <OutputList name="hPhrasesCode" label="substances.hPhrases"/>
                <ShowBasedOnConfig configKey={SUBSTANCE_ADR_VISIBLE}>
                    <OutputText name="adr" label="substances.adr"/>
                </ShowBasedOnConfig>
            </Vertical>
        </Horizontal>
    </View>
);

const exportButtons = (filters, context, toggleModal) => {
    return (
        <ShowBasedOnConfig configKey={SDS_EXPORT_ENABLED}>
            <DownloadButton from={ENTITY_NAME}
                            filters={filters}
                            type="application/vnd.ms-excel"
                            extension="xls"
                            tooltip="substances.export"
                            wide={true}
                            handleError={toggleModal}
            />
        </ShowBasedOnConfig>
    );
};

const CrudList = () => {
    const table = <CollapsibleRowList optionLabelName="code"
                                      hideEditButton={!withRoles(['A', 'B', 'C'])}
                                      showDeleteButton={withRoles(['A', 'B', 'C'])}
                                      rowMarker={rowMarker}
    >
        <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>
            <OutputBox name="code" label="substances.idCode" style={{flex: 1, flexBasis: 'auto'}}/>
            <OutputBox name="supplierCode" label="substances.supplier.code" style={{flex: 2, flexBasis: 'auto'}}/>
            <OutputBox name="agentDescription" label="substances.agent" style={{flex: 2, flexBasis: 'auto'}}/>
            <OutputBox name="physicalState" label="substances.physicalState" style={{flex: 1, flexBasis: 'auto'}}/>
            <OutputBox name="supplier" label="substances.supplier.name" style={{flex: 1, flexBasis: 'auto'}}/>
        </Horizontal>
        <ShowBasedOnConfig configKey={R_PHRASES_AND_SCORE_P_VISIBLE}>
            <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>
                <OutputBoxContent label="substances.rPhrases" style={{flex: 3}}>
                    <SimpleOutputList name="rPhrasesCode"/>
                </OutputBoxContent>
                <OutputBox name="scoreP" label="substances.pScore" style={{flex: 2}}/>
            </Horizontal>
        </ShowBasedOnConfig>
        <Horizontal>
            <OutputBoxContent label="substances.hPhrases" style={{flex: 3}}>
                <SimpleOutputList name="hPhrasesCode"/>
            </OutputBoxContent>
            <OutputBox name="scoreH" label="substances.hScore"
                       style={{flex: 2, backgroundColor: 'rgb(248, 252, 253)'}}/>
        </Horizontal>
        <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>
            <OutputBoxBoolean name="cutaneousRisk" label="substances.cutaneousRisk" style={{flex: 1}}/>
            <OutputBoxBoolean name="inhalationRisk" label="substances.inhalationRisk" style={{flex: 1}}/>
            <OutputBoxBoolean name="cancerousRisk" label="substances.cancerousRisk" style={{flex: 1}}/>
            <OutputBoxBoolean name="mutagenRisk" label="substances.mutagenRisk" style={{flex: 1}}/>
            <OutputBoxBoolean name="teratogenRisk" label="substances.teratogenRisk" style={{flex: 1}}/>
            <OutputBoxBoolean name="atexSubstance" label="substances.atexSubstance" style={{flex: 1}}/>
            <ShowBasedOnConfig configKey={SUBSTANCE_ADR_VISIBLE}>
                <OutputBox name="adr" label="substances.adr" style={{flex: 1}}/>
            </ShowBasedOnConfig>
        </Horizontal>

        {/* SDS */}
        <Horizontal style={{
            alignItems: 'center',
            backgroundColor: 'rgb(238, 242, 246)',
            borderLeft: '1px solid #7F8FA4',
            borderRight: '1px solid #7F8FA4',
            borderBottom: '1px solid #7F8FA4',
        }}>
            <Horizontal style={{
                fontSize: '0.9em',
                fontWeight: 700,
                color: '#7F8FA4',
                padding: '10px 5px',
            }}>
                <LastSafetyDataSheetText style={{marginRight: 10}}/>
                <OutputValue name="lastSafetyDataSheet.creationTimestamp" defaultValue="---"/>
                <OutputValue name="lastSafetyDataSheetVersion" className="last-evaluation-index" hideIf={0}/>
            </Horizontal>
            <ViewSafetyDataSheetButton/>
            <WithRole roles={['A', 'B', 'C']}>
                <NewSafetyDataSheetButton/>
            </WithRole>
            <ArchiveSafetyDataSheetButton/>
        </Horizontal>

        <Vertical rendered="lastSafetyDataSheet">
            <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>
                <ShowBasedOnCondition evaluateOnField="lastSafetyDataSheet" condition={v => v.downloadable === true}>
                    <OutputBoxContent label="substances.sds" style={{flex: 1}}>
                        <OutputLink name="lastSafetyDataSheet.sds"/>
                    </OutputBoxContent>
                </ShowBasedOnCondition>
                <ShowBasedOnCondition evaluateOnField="lastSafetyDataSheet" condition={v => v.downloadable === false}>
                    <OutputBoxContent label="substances.sds" style={{flex: 1}}>
                        <OutputValue name="lastSafetyDataSheet.sds" style={{color: '#c7c7c7'}}/>
                    </OutputBoxContent>
                </ShowBasedOnCondition>
                <ShowBasedOnCondition evaluateOnField="lastSafetyDataSheet" condition={v => v.downloadable === true}>
                    <OutputBoxContent label="substances.attachments" style={{flex: 2}}>
                        <Iterate on="lastSafetyDataSheet.attachments">
                            <OutputAttachmentRow/>
                        </Iterate>
                    </OutputBoxContent>
                </ShowBasedOnCondition>
                <ShowBasedOnCondition evaluateOnField="lastSafetyDataSheet" condition={v => v.downloadable === false}>
                    <OutputBoxContent label="substances.attachments" style={{flex: 2}}>
                        <Iterate on="lastSafetyDataSheet.attachments">
                            <OutputAttachmentRowDisabled/>
                        </Iterate>
                    </OutputBoxContent>
                </ShowBasedOnCondition>
            </Horizontal>
        </Vertical>

        {/*SDS EVALUATIONS*/}
        <Vertical rendered="lastSafetyDataSheet">
            <Horizontal style={{
                alignItems: 'center',
                backgroundColor: 'rgb(238, 242, 246)',
                borderLeft: '1px solid #7F8FA4',
                borderRight: '1px solid #7F8FA4',
                borderBottom: '1px solid #7F8FA4',
            }}>
                <Horizontal style={{
                    fontSize: '0.9em',
                    fontWeight: 700,
                    color: '#7F8FA4',
                    padding: '10px 5px',
                }}>
                    <LastEvaluationText style={{marginRight: 10}}/>
                    <OutputValue name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.evaluationTimestamp"
                                 defaultValue="---"/>
                    {/* <OutputValue name="lastSafetyDataSheet.lastSafetyDataSheetEvaluationIndex" className="last-evaluation-index" hideIf={0} /> */}
                </Horizontal>
                <ViewSafetyDataSheetEvaluationButton/>
                <WithRole roles={['A', 'B']}>
                    <NewSafetyDataSheetEvaluationButton/>
                </WithRole>
            </Horizontal>
        </Vertical>

        <ShowBasedOnConfig configKey={FULL_SDS_EVALUATION_ENABLED}>
            <Vertical rendered="lastSafetyDataSheet.lastSafetyDataSheetEvaluation">
                <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>
                    <OutputBoxContent label="substances.sdsEvaluationConsiderationCLPShort" style={{flex: 1}}>
                        <LocalizedOutputValue name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.clp"
                                              prefix="substances.sdsEvaluationConsiderationCLPValues"/>
                    </OutputBoxContent>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.italianSds"
                                      label="substances.sdsEvaluationItalianSdsShort" style={{flex: 1}}/>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.pageNumeration"
                                      label="substances.sdsEvaluationPageNumerationShort" style={{flex: 1}}/>
                </Horizontal>
            </Vertical>
        </ShowBasedOnConfig>
        <ShowBasedOnConfig configKey={FULL_SDS_EVALUATION_ENABLED}>
            <Vertical rendered="lastSafetyDataSheet.lastSafetyDataSheetEvaluation">
                <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.sectionsCompleted"
                                      label="substances.sdsEvaluationSectionsCompletedShort" style={{flex: 1}}/>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.sectionsWithoutText"
                                      label="substances.sdsEvaluationSectionsWithoutTextShort" style={{flex: 1}}/>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.section1"
                                      label="substances.sdsEvaluationSection1Short" style={{flex: 1}}/>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.section2"
                                      label="substances.sdsEvaluationSection2Short" style={{flex: 1}}/>
                </Horizontal>
            </Vertical>
        </ShowBasedOnConfig>
        <ShowBasedOnConfig configKey={FULL_SDS_EVALUATION_ENABLED}>
            <Vertical rendered="lastSafetyDataSheet.lastSafetyDataSheetEvaluation">
                <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.compliantSds"
                                      label="substances.sdsEvaluationCompliantSdsShort" style={{flex: 1}}/>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.usableSds"
                                      label="substances.sdsEvaluationUsableSdsShort" style={{flex: 1}}/>
                    <OutputBoxContent label="substances.sdsEvaluationNoteShort" style={{flex: 3}}>
                        <OutputValue name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.note"/>
                    </OutputBoxContent>
                </Horizontal>
            </Vertical>
        </ShowBasedOnConfig>

        <ShowBasedOnConfig configKey={FULL_SDS_EVALUATION_ENABLED} enabledOn={false}>
            <Vertical rendered="lastSafetyDataSheet.lastSafetyDataSheetEvaluation">
                <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>
                    <OutputBoxContent label="substances.sdsEvaluationConsiderationCLPShort" style={{flex: 1}}>
                        <LocalizedOutputValue name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.clp"
                                              prefix="substances.sdsEvaluationConsiderationCLPValues"/>
                    </OutputBoxContent>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.italianSds"
                                      label="substances.sdsEvaluationItalianSdsShort" style={{flex: 1}}/>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.sectionsCompleted"
                                      label="substances.sdsEvaluationSectionsCompletedShort" style={{flex: 1}}/>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.compliantSds"
                                      label="substances.sdsEvaluationCompliantSdsShort" style={{flex: 1}}/>
                    <OutputBoxBoolean name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.usableSds"
                                      label="substances.sdsEvaluationUsableSdsShort" style={{flex: 1}}/>
                </Horizontal>
            </Vertical>
        </ShowBasedOnConfig>
        <ShowBasedOnConfig configKey={FULL_SDS_EVALUATION_ENABLED} enabledOn={false}>
            <Vertical rendered="lastSafetyDataSheet.lastSafetyDataSheetEvaluation">
                <Horizontal style={{backgroundColor: 'rgb(248, 252, 253)'}}>

                    <OutputBoxContent label="substances.sdsEvaluationNoteShort" style={{flex: 1}}>
                        <OutputValue name="lastSafetyDataSheet.lastSafetyDataSheetEvaluation.note"/>
                    </OutputBoxContent>
                </Horizontal>
            </Vertical>
        </ShowBasedOnConfig>


    </CollapsibleRowList>;

    return <List title={TITLE}
                 entityName={ENTITY_NAME}
                 entityRoute={ENTITY_NAME}
                 component={table}
                 filters={defaultFilters()}
                 additionalButtons={exportButtons}
                 hideAddButton={!withRoles(['A', 'B', 'C'])}
    />;
};

const rowMarker = (resource) => {
    if (resource.lastSafetyDataSheet != null) {
        return ((resource.lastSafetyDataSheet.toBeEvaluated ? 'to-be-evaluated' : '') + ' '
            + (resource.lastSafetyDataSheet.approved ? '' : 'sds-rejected'));
    } else {
        return ('');
    }
};

const Substances = props => {
    const basePath = props.match.url;

    return (
        <MainContainer>
            <Switch>
                <Route exact path={basePath} component={CrudList}/>
                <Route exact path={basePath + '/searchResult'} component={SearchResultPanelSDS}/>
                <Route exact path={basePath + '/view/:number'} component={CrudView}/>
                <Route exact path={basePath + '/edit/:number'} render={props => <CrudEdit {...props} />}/>
                <Route exact path={basePath + '/add'} render={props => <CrudEdit {...props} />}/>
                <Route path={basePath + '/:number/safetyDataSheet'} component={SafetyDataSheets}/>
                <Route path={basePath + '/:number/safetyDataSheetEvaluation'} component={SafetyDataSheetEvaluations}/>
            </Switch>
        </MainContainer>
    );
};

export default Substances;



// WEBPACK FOOTER //
// ./src/main/pages/Substances.js